import React from "react";
import SectionHeading from "../components/SectionHeading";
import { Trophy, Award, Star, Crown, Baby } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

/**
 * When Pigs Fly Family BBQ — 2-year competition record.
 * Grouped chronologically; grand-championship results tagged distinctively.
 */
const AWARDS_BY_YEAR = [
  {
    year: "2026",
    highlights: [
      { label: "Exotic Champion — Quail (Torry Island)", icon: Star },
      { label: "Kids Q Grand Champion — Jace (Torry Island)", icon: Baby },
      { label: "Kids Q Grand Champion — Berkley (4 Justice Off-Road BBQ)", icon: Baby },
    ],
    events: [
      {
        name: "4 Justice Off-Road BBQ",
        placings: [
          { cat: "Brisket", place: "1st" },
          { cat: "Pork", place: "2nd" },
          { cat: "Ribs", place: "2nd" },
          { cat: "Chicken", place: "3rd" },
        ],
      },
      {
        name: "4 Justice Off-Road BBQ · Kids Q",
        placings: [
          { cat: "Berkley", place: "Grand Champion" },
          { cat: "Jace", place: "3rd" },
        ],
      },
      {
        name: "Torry Island",
        placings: [
          { cat: "Ribs", place: "4th" },
          { cat: "Chicken", place: "8th" },
        ],
      },
      {
        name: "Torry Island · Exotic",
        placings: [{ cat: "Quail", place: "Champion" }],
      },
      {
        name: "Torry Island · Kids Q",
        placings: [
          { cat: "Jace", place: "Grand Champion" },
          { cat: "Team Berkley", place: "2nd" },
        ],
      },
      {
        name: "Lakeland Pigfest",
        placings: [
          { cat: "Pork", place: "3rd" },
          { cat: "Ribs", place: "7th" },
        ],
      },
    ],
  },
  {
    year: "2025",
    highlights: [
      { label: "Grand Champion — Jimmy Jam", icon: Crown },
      { label: "Grand Champion — Torry Island", icon: Crown },
      { label: "Reserve Grand Champion — Haines City", icon: Award },
      { label: "Exotic Champion — Alligator (Torry Island)", icon: Star },
    ],
    events: [
      {
        name: "Jimmy Jam · Grand Champion",
        placings: [
          { cat: "Brisket", place: "1st" },
          { cat: "Pork", place: "1st" },
          { cat: "Ribs", place: "4th" },
          { cat: "Chicken", place: "6th" },
        ],
      },
      {
        name: "Torry Island · Grand Champion",
        placings: [
          { cat: "Ribs", place: "1st" },
          { cat: "Chicken", place: "2nd" },
        ],
      },
      {
        name: "Haines City · Reserve Grand Champion",
        placings: [{ cat: "Brisket", place: "1st" }],
      },
      {
        name: "Torry Island · Exotic",
        placings: [{ cat: "Alligator", place: "Champion" }],
      },
      { name: "Pig Jam", placings: [{ cat: "Pork", place: "1st" }] },
      {
        name: "Lakeland Pigfest",
        placings: [
          { cat: "Brisket", place: "2nd" },
          { cat: "Ribs", place: "3rd" },
        ],
      },
      { name: "Barbecue Bash", placings: [{ cat: "Pork", place: "3rd" }] },
      {
        name: "Big Belly Barbecue Bash",
        placings: [
          { cat: "Chicken", place: "3rd" },
          { cat: "Pork", place: "8th" },
        ],
      },
    ],
  },
  {
    year: "2024",
    highlights: [],
    events: [
      {
        name: "Pig Jam",
        placings: [
          { cat: "Ribs", place: "2nd" },
          { cat: "Pork", place: "3rd" },
          { cat: "Brisket", place: "10th" },
        ],
      },
    ],
  },
];

// Highlighted place styles for grand results
function placeStyle(place) {
  const p = place.toLowerCase();
  if (p.includes("grand") || p.includes("champion") || p === "1st" || p === "reserve") return "bg-ink text-bone";
  if (p === "2nd" || p === "3rd") return "bg-bone text-ink border border-ink";
  return "bg-transparent text-ink/70 border border-ink/30";
}

export default function Events() {
  usePageMeta({
    title: "Awards & Events",
    description: "Championship BBQ trophies, competition wins and upcoming events from When Pigs Fly Family BBQ — Florida's Nature Coast pit crew.",
    path: "/events",
  });
  return (
    <div>
      {/* HERO */}
      <section className="border-b border-ink py-16 md:py-24" data-testid="events-hero">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="eyebrow mb-3 text-ink/70">Championship record</div>
          <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">
            Awards &amp; Events
          </h1>
          <p className="mt-6 max-w-2xl font-serif text-base leading-relaxed text-ink/80">
            Two years. One family. A trophy case that keeps outgrowing itself. Every ribbon below was earned on-site,
            on the smoker, by our team. Real BBQ. Made the Florida Man Way.
          </p>
        </div>
      </section>

      {/* AWARDS / TROPHY WALL */}
      <section className="border-b border-ink bg-bone py-20 md:py-28" data-testid="awards-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="The trophy wall" title="Our Competition Record" subtitle="Grand Champions, Reserve Grands, Exotic titles, and Kids Q wins — from 2024 to today." />

          <div className="mt-14 space-y-16">
            {AWARDS_BY_YEAR.map((yr) => (
              <div key={yr.year} data-testid={`awards-year-${yr.year}`}>
                {/* Year header */}
                <div className="mb-6 flex items-end justify-between border-b-2 border-ink pb-3">
                  <h3 className="font-rye text-5xl uppercase leading-none md:text-7xl">{yr.year}</h3>
                  <span className="font-mono text-xs uppercase tracking-[0.24em] text-ink/60">
                    {yr.events.length} competition{yr.events.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Highlights strip */}
                {yr.highlights.length > 0 && (
                  <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {yr.highlights.map((h, i) => {
                      const Ico = h.icon || Trophy;
                      return (
                        <div key={i} className="flex items-center gap-3 border-2 border-ink bg-ink px-4 py-3 text-bone shadow-hard">
                          <Ico size={18} className="shrink-0" />
                          <span className="font-alfa text-sm uppercase leading-tight">{h.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Events grid */}
                <div className="grid grid-cols-1 gap-0 border border-ink bg-white md:grid-cols-2 lg:grid-cols-3">
                  {yr.events.map((ev, i) => (
                    <div
                      key={i}
                      data-testid={`event-${yr.year}-${i}`}
                      className={`grain-block flex flex-col p-6 border-ink ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""} ${(i + 1) % 2 !== 0 ? "md:border-r lg:border-r-0" : ""} ${i < yr.events.length - 1 ? "border-b" : ""}`}
                    >
                      <div className="mb-4 flex items-center gap-2 border-b border-ink/30 pb-3">
                        <Trophy size={14} className="shrink-0" />
                        <h4 className="font-alfa text-base uppercase leading-tight">{ev.name}</h4>
                      </div>
                      <ul className="space-y-2">
                        {ev.placings.map((p, j) => (
                          <li key={j} className="flex items-center justify-between gap-3">
                            <span className="font-serif text-sm text-ink/80">{p.cat}</span>
                            <span className={`px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${placeStyle(p.place)}`}>
                              {p.place}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS — COMING SOON */}
      <section className="border-b border-ink py-20 md:py-28" data-testid="upcoming-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="On the road" title="Upcoming Events" subtitle="Where you can catch the smoker next." />

          <div className="mt-12 border-2 border-ink bg-white p-10 text-center shadow-hard md:p-16" data-testid="events-coming-soon">
            <div className="eyebrow mb-3 text-ink/60">Stay tuned</div>
            <h3 className="font-rye text-4xl uppercase leading-none sm:text-5xl md:text-6xl">Coming Soon</h3>
            <p className="mx-auto mt-6 max-w-lg font-serif text-base leading-relaxed text-ink/80">
              We're finalizing our 2026 competition and event schedule. Follow us on Instagram
              @Whenpigsfly2guysgrilling or check back here for updates.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
